import { price as priceUtil } from '@eti/utils';
import { css } from '@eti/styles';
import { Card, Inline, Stack, Text, textVariants } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { ScreenReaderPrice } from '@eti/components';
import { getAgeTypeLabel, getAgeTypeLabelForQuantity } from '../../../../../../utils/travelers';
import type { TravelerDisplayPricesType } from '../utils';
import CarrierPriceList from './CarrierPriceList';

const inlineStyles = css`
  justify-content: space-between;
`;

const stackStyles = css`
  padding-inline-start: 8px;
`;

interface TravelersPerCarrierProps {
  travelerPrices: TravelerDisplayPricesType[];
}

const TravelersPerCarrier = ({ travelerPrices = [] }: TravelersPerCarrierProps) => {
  const { t } = useTranslation();

  const travelerPricesInfo = travelerPrices.map(
    ({
      ageType,
      providerPricePerCarrier,
      taxesAndFees,
      quantity,
      price,
      totalPrice,
      totalPriceSum,
      vat,
    }) => {
      const ageTypeLabelForQuantity = getAgeTypeLabelForQuantity(ageType, quantity);
      const ageTypeSingularLabel = t(getAgeTypeLabel(ageType)).toLowerCase();
      const ageTypeLabel = t(ageTypeLabelForQuantity, quantity);

      return {
        ageType,
        ageTypeLabel,
        providerPricePerCarrier,
        costLabel: t('Cart.CostPerAgeType', ageTypeSingularLabel),
        discountLabel: t('Cart.DiscountPerAgeType', ageTypeSingularLabel),
        price,
        priceLabel: t('Cart.PricePerAgeType', ageTypeSingularLabel),
        taxesAndFees,
        totalPrice,
        totalPriceSum,
        // TODO: Update the text key https://etraveligroup.atlassian.net/browse/MYP-1977
        totalPriceSumLabel: t('TripDetails.PriceBreakdown.TravelerGroupPriceSummary', ageTypeLabel),
        totalLabel: t('Cart.TotalPerAgeType', ageTypeSingularLabel),
        vatLabel: t('Cart.TaxesAndAirlineFees'),
        vat,
      };
    },
  );

  return (
    <>
      {travelerPricesInfo.map(
        ({
          ageType,
          ageTypeLabel,
          providerPricePerCarrier,
          price,
          priceLabel,
          totalPrice,
          totalLabel,
          totalPriceSum,
          totalPriceSumLabel,
          vat,
          vatLabel,
        }) => {
          return (
            <Card.Section key={ageType}>
              <Stack data-testid={`priceSpecification-traveler-${ageType}-price-info`} spacing={8}>
                <Text
                  data-testid={`priceSpecification-traveler-${ageType}-ageType-label`}
                  variant={textVariants._14_BOLD}
                >
                  {ageTypeLabel}
                </Text>
                {providerPricePerCarrier && (
                  <Stack
                    className={stackStyles}
                    data-testid={`priceSpecification-traveler-${ageType}-carrier-breakdown`}
                    spacing={8}
                  >
                    <CarrierPriceList
                      amountType="providerPrice"
                      carrierPrices={providerPricePerCarrier}
                      dataTestId={`providerPrice-per-carrier-traveler-${ageType}`}
                      heading={priceLabel}
                      price={price}
                    />
                    <CarrierPriceList
                      amountType="vat"
                      carrierPrices={providerPricePerCarrier}
                      dataTestId={`vat-per-carrier-traveler-${ageType}`}
                      heading={vatLabel}
                      price={vat}
                    />
                  </Stack>
                )}
                <Stack className={stackStyles} spacing={0}>
                  <Inline className={inlineStyles}>
                    <Text data-testid={`priceSpecification-traveler-${ageType}-totalPrice-label`}>
                      {totalLabel}
                    </Text>
                    <Text
                      aria-hidden
                      data-testid={`priceSpecification-traveler-${ageType}-totalPrice`}
                    >
                      {priceUtil.formatPrice(totalPrice)}
                    </Text>
                    <ScreenReaderPrice price={priceUtil.formatPrice(totalPrice)} />
                  </Inline>
                  <Inline className={inlineStyles}>
                    <Text
                      data-testid={`priceSpecification-traveler-${ageType}-totalPriceSum-label`}
                      variant={textVariants._14_BOLD}
                    >
                      {totalPriceSumLabel}
                    </Text>
                    <Text
                      aria-hidden
                      data-testid={`priceSpecification-traveler-${ageType}-totalPriceSum`}
                      variant={textVariants._14_BOLD}
                    >
                      {priceUtil.formatPrice(totalPriceSum)}
                    </Text>
                    <ScreenReaderPrice price={priceUtil.formatPrice(totalPriceSum)} />
                  </Inline>
                </Stack>
              </Stack>
            </Card.Section>
          );
        },
      )}
    </>
  );
};

export default TravelersPerCarrier;
